'use client'
import { type FC, useEffect, useRef } from 'react'
import { Box, VStack } from '@chakra-ui/react'
import { BiCheckCircle } from 'react-icons/bi'
import { GrandHeading, GrandText } from 'ui'
import { OverridesContextProvider, type OverridesContextType, overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { type OrderDTO, PaymentType } from 'ecosystem'
import { getInvoicePaymentTranslations } from '../CheckoutPayment/InvoicePayment'

export interface InvoiceConfirmationOverrides {
  invoiceConfirmationTitle?: string
  invoiceConfirmationOrderNumberPrefix?: string
  invoiceConfirmationBody?: string
  invoiceConfirmationBodyProforma?: string
}

interface InvoiceConfirmationProps {
  order: OrderDTO
  onConfirmPurchase?: () => void
}

const Inner: FC<InvoiceConfirmationProps> = ({ order, onConfirmPurchase }) => {
  const overrides = useOverridesContext<keyof InvoiceConfirmationOverrides>()
  const firedRef = useRef(false)

  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true
    onConfirmPurchase?.()
  }, [onConfirmPurchase])

  return (
    <VStack align="center" spacing={4} textAlign="center">
      <Box color="status.success">
        <BiCheckCircle size={64} />
      </Box>

      <GrandHeading
        headingTag="h2"
        title={overrideText('Tack för din beställning!', overrides?.invoiceConfirmationTitle)}
      />

      {order.orderNumber ? (
        <GrandText fontWeight="semibold">
          {overrideText('Ordernummer: ', overrides?.invoiceConfirmationOrderNumberPrefix)}
          {order.orderNumber}
        </GrandText>
      ) : null}

      <GrandText color="text.mild" maxW="md">
        {order.paymentType === PaymentType.PROFORMA
          ? overrideText(
              'En proformafaktura skickas till företaget. Beställningen behandlas när betalningen har mottagits. Du kan följa orderns status under Mina beställningar.',
              overrides?.invoiceConfirmationBodyProforma
            )
          : overrideText(
              'En faktura skickas till företaget enligt avtalade villkor. Du kan följa orderns status under Mina beställningar.',
              overrides?.invoiceConfirmationBody
            )}
      </GrandText>
    </VStack>
  )
}

/**
 * Wraps `Inner` in an `OverridesContextProvider` that supplies locale-aware
 * translations as defaults. Brand-level overrides set higher up the tree still
 * win because the context provider merges parent + own overrides.
 */
export const InvoiceConfirmation: FC<InvoiceConfirmationProps> = (props) => {
  return (
    <OverridesContextProvider
      overrides={getInvoicePaymentTranslations() as unknown as OverridesContextType}
    >
      <Inner {...props} />
    </OverridesContextProvider>
  )
}

export default InvoiceConfirmation
